/**
 * Scenario summary sentence (Planning Resource Model PRD, Part E).
 *
 * Mid-walkthrough on 2026-08-24 the scenario setup puzzled the person who built
 * it — which predicts exactly how it lands on someone new. Every clause below is
 * assembled from state the scenario already carries; nothing here is new
 * machinery, it is the run's own configuration said out loud in one sentence,
 * with each clause linking to where it is changed.
 *
 * Pure: no React, no Supabase.
 */

import { PLANNING_MODE_LABELS } from './planningEngineModule.types';
import type { PlanningMode, Scenario } from './planningEngineModule.types';

export interface ScenarioSummaryClause {
  /** Stable key for rendering. */
  key: string;
  /** Lead-in text, e.g. "scoped to". */
  lead: string;
  /** The bound value, rendered emphasised. */
  value: string;
  /** Trailing text, e.g. "(falling back to priority estimates)". */
  trail?: string;
  /** Where this clause is changed. Absent when the control has no route of its own. */
  href?: string;
  /** Hover text naming the control that owns this clause. */
  title: string;
}

/** How each planning mode reads effort, in the planner's own words. */
export function effortSourcePhrase(mode: PlanningMode): { value: string; trail?: string } {
  switch (mode) {
    case 'work_units':
      return { value: 'work units', trail: '(priorities without work units cannot be planned)' };
    case 'priority_estimate':
      return { value: 'priority estimates', trail: '(work-unit detail is ignored)' };
    case 'hybrid_remaining':
      return {
        value: 'work units',
        trail: 'plus a remaining estimate where a priority is undecomposed',
      };
  }
}

export function buildScenarioSummaryClauses(args: {
  scenario: Scenario | null;
  /** Human label for the bound Engine Input Scope, e.g. "Published List: Cloud Servers". */
  scopeLabel: string;
  /** Plan cycle name, when the scenario is bound to one and the name has resolved. */
  planCycleName?: string | null;
  /** Route prefix, so mobile and desktop link to their own surfaces. */
  basePath?: string;
}): ScenarioSummaryClause[] {
  const { scenario, scopeLabel, planCycleName, basePath = '/planning/engine' } = args;
  if (!scenario) return [];

  const scenariosHref = `${basePath}/scenarios`;
  const effort = effortSourcePhrase(scenario.planningMode);

  const clauses: ScenarioSummaryClause[] = [
    {
      key: 'effort',
      lead: 'Reads effort from',
      value: effort.value,
      trail: effort.trail,
      href: scenariosHref,
      title: `Planning mode: ${PLANNING_MODE_LABELS[scenario.planningMode]} — change it on the scenario`,
    },
    {
      key: 'scope',
      lead: 'scoped to',
      value: scopeLabel,
      title: 'Input Scope — change it in the scope bar above',
    },
  ];

  if (scenario.planCycleId) {
    clauses.push({
      key: 'cycle',
      lead: 'bound to',
      value: planCycleName ? `Cycle ${planCycleName}` : 'a plan cycle',
      href: scenariosHref,
      title: 'Plan cycle binding — change it in the scenario picker',
    });
  } else {
    clauses.push({
      key: 'cycle',
      lead: 'running as',
      value: 'a what-if',
      trail: 'not bound to a plan cycle, so it cannot be committed',
      href: scenariosHref,
      title: 'Plan cycle binding — change it in the scenario picker',
    });
  }

  clauses.push(
    {
      key: 'horizon',
      lead: 'over a',
      value: `${scenario.planningWeeks}-week horizon`,
      href: scenariosHref,
      title: 'Planning weeks — change it on the scenario',
    },
    {
      key: 'buffer',
      lead: 'with a',
      value: `${Math.round(scenario.capacityBufferPercent * 100)}% capacity buffer`,
      href: scenariosHref,
      title: 'Capacity buffer — the conservative bound behind the at-risk band',
    },
    {
      key: 'wu-hours',
      lead: 'counting',
      value: `${scenario.defaultWorkUnitHours} h to a work unit`,
      href: scenariosHref,
      title: 'Work-unit hours — change it on the scenario',
    },
  );

  // Only say the include switches out loud when they are off the default, so
  // the sentence stays short in the common case.
  const included: string[] = [];
  if (scenario.includeNotReady) included.push('not-ready');
  if (scenario.includeBlocked) included.push('blocked');
  if (included.length > 0) {
    clauses.push({
      key: 'includes',
      lead: 'and planning',
      value: `${included.join(' and ')} work as if it were ready`,
      href: scenariosHref,
      title: 'Include Not Ready / Include Blocked — change them on the scenario',
    });
  }

  return clauses;
}

/** The same sentence as plain text — for tooltips, exports, and tests. */
export function scenarioSummarySentence(clauses: ScenarioSummaryClause[]): string {
  if (clauses.length === 0) return '';
  const body = clauses
    .map((c) => [c.lead, c.value, c.trail].filter(Boolean).join(' '))
    .join(' · ');
  return `${body}.`;
}
